import { useEffect } from 'react';
import { useMutation } from 'platform/useMutation';
import { gql, MutationHookOptions } from '@apollo/client';

import {
  RequestLateCheckout,
  RequestLateCheckoutVariables,
} from 'sellDirect/graphql/__generated__/RequestLateCheckout';
import { useObservability } from 'shared/helpers/observability';
import { LocalDate } from '@js-joda/core';
import { useOfferContext } from 'sellDirect/OfferContext';

const RequestLateCheckoutMutation = gql`
  mutation RequestLateCheckout($webOfferToken: String!, $lateCheckoutDate: String!) {
    seller {
      requestLateCheckout(webOfferToken: $webOfferToken, lateCheckoutDate: $lateCheckoutDate) {
        id
        complete
        completedAt
        downloadUrl
        previewUrl
        state
        taskType
        taskable {
          ... on LateCheckoutTask {
            costPerDayCents
            fixedCostCents
            lateCheckoutDate
            numberOfDays
            sellerInterested
            totalCostCents
          }
        }
      }
    }
  }
`;

/**
 * Hook that allows us to request a late checkout
 *
 * @param onCompleted - Optional callback to call after the request late checkout request completes
 * @param onError - Optional callback to call if there's an error making the request
 * @param errorPolicy - Optional error policy to use
 */
export const useRequestLateCheckout = (options?: MutationHookOptions<RequestLateCheckout>) => {
  const { offer } = useOfferContext();
  const { trackError } = useObservability();

  const [request, { data, loading, error }] = useMutation<
    RequestLateCheckout,
    RequestLateCheckoutVariables
  >(RequestLateCheckoutMutation, {
    onCompleted: options?.onCompleted,
    onError: options?.onError,
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: ['GetSellDirect'],
    errorPolicy: options?.errorPolicy,
  });

  const requestLateCheckout = (lateCheckoutDate: LocalDate) => {
    if (!offer?.leadToken) {
      trackError('missing-web-offer-token');
      return;
    }

    return request({
      variables: {
        webOfferToken: offer?.leadToken,
        lateCheckoutDate: lateCheckoutDate.toString(),
      },
    }).catch((error) => {
      trackError('request-late-checkout', error, {
        lateCheckoutDate: lateCheckoutDate.toString(),
      });
    });
  };

  useEffect(() => {
    if (error) {
      trackError('request-late-checkout');
    }
    // eslint-disable-next-line
  }, [error]);

  return { requestLateCheckout, loading, data, error };
};
